import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "@mantine/core";
import swal from "sweetalert";
import schedulerService from "../../../services/scheduler";
import { DOW, MON_SHORT, MONTHS, addDays, startOfWeek, sameDay, fmtRange, initials, isoDate } from "./scheduleUtils";
import Icon from "./icons";
import "./SchedulerCreate.css";

const pad = (n) => String(n).padStart(2, "0");
const keyOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (v) => {
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const mdy = (d) => `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
const toMin = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};
const minToHHMM = (m) => `${pad(Math.floor((m % 1440) / 60))}:${pad((m % 1440) % 60)}`;
const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);
const isConsecutive = (ds) => {
  for (let i = 1; i < ds.length; i++) if (ds[i] - ds[i - 1] !== 86400000) return false;
  return true;
};

/* ---------------- custom date picker (cdate) ---------------- */
export function CDatePicker({ dates, onChange, isAppt, multi = false, placeholder = "Select date(s)" }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(isAppt ? "custom" : "week");
  const [viewMonth, setViewMonth] = useState(() => {
    const d = dates.length ? parseISO(dates[dates.length - 1]) : new Date();
    d.setDate(1);
    return d;
  });
  const [sel, setSel] = useState(() => new Set());
  const [weekLock, setWeekLock] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  const monStart = (d) => {
    const x = new Date(d);
    x.setDate(x.getDate() - x.getDay());
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const toggle = (e) => {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    setSel(new Set());
    setWeekLock(null);
    setMode(isAppt ? "custom" : "week");
    const d = dates.length ? parseISO(dates[dates.length - 1]) : new Date();
    d.setDate(1);
    setViewMonth(d);
    setOpen(true);
  };

  const clickDay = (d) => {
    const k = keyOf(d);
    const next = new Set(sel);
    if (mode === "week") {
      const ws = keyOf(monStart(d));
      if (weekLock && weekLock !== ws) return;
      if (next.has(k)) {
        next.delete(k);
        if (next.size === 0) setWeekLock(null);
      } else {
        next.add(k);
        setWeekLock(ws);
      }
    } else if (next.has(k)) {
      // click an already-selected day → unselect it
      next.delete(k);
    } else if (next.size === 1) {
      // second click fills the continuous range between the two days
      const a = parseISO([...next][0]);
      const lo = a < d ? a : d;
      const hi = a < d ? d : a;
      let cd = new Date(lo);
      while (cd <= hi) {
        next.add(keyOf(cd));
        cd = addDays(cd, 1);
      }
    } else if (multi) {
      // additive: keep the existing selection and add this separate day, so
      // non-continuous days can be picked (e.g. Jul 7, Jul 10, Jul 15)
      next.add(k);
    } else {
      next.clear();
      next.add(k);
    }
    setSel(next);
  };

  const apply = (e) => {
    e.stopPropagation();
    const picked = [...sel].sort();
    if (picked.length) onChange(picked);
    setOpen(false);
  };

  const setModePreset = (m, e) => {
    e.stopPropagation();
    setMode(m);
    setSel(new Set());
    setWeekLock(null);
  };

  // label + pills come from the committed `dates`
  const dObjs = dates.map(parseISO).sort((a, b) => a - b);
  let label;
  if (dObjs.length === 0) {
    label = placeholder;
  } else if (dObjs.length === 1) {
    const d = dObjs[0];
    label = `${DOW[d.getDay()]}, ${MON_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } else if (isConsecutive(dObjs)) {
    label = `${mdy(dObjs[0])} – ${mdy(dObjs[dObjs.length - 1])}`;
  } else {
    label = `${dObjs.length} dates`;
  }
  const showPills = dObjs.length > 1 && !isConsecutive(dObjs);

  const removePill = (e, k) => {
    e.stopPropagation();
    const next = dates.filter((x) => x !== k);
    onChange(next.length ? next : dates);
  };

  // build 6x7 grid
  const base = new Date(viewMonth);
  const gridStart = (() => {
    const x = new Date(base.getFullYear(), base.getMonth(), 1);
    x.setDate(x.getDate() - x.getDay());
    x.setHours(0, 0, 0, 0);
    return x;
  })();
  const today = new Date();
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    const k = keyOf(d);
    const out = d.getMonth() !== base.getMonth();
    const isToday = sameDay(d, today);
    const isSel = sel.has(k);
    const dow = d.getDay();
    const prevSel = sel.has(keyOf(addDays(d, -1)));
    const nextSel = sel.has(keyOf(addDays(d, 1)));
    const isStart = !prevSel || dow === 0;
    const isEnd = !nextSel || dow === 6;
    const offweek = mode === "week" && weekLock && keyOf(monStart(d)) !== weekLock;
    const cls = [
      "dp-cell",
      out ? "out" : "",
      isToday ? "today" : "",
      isSel ? `in-week ${isStart ? "wk-start" : ""} ${isEnd ? "wk-end" : ""}` : "",
      offweek ? "dp-off" : "",
    ]
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    cells.push(
      <button key={i} type="button" className={cls} onClick={(e) => { e.stopPropagation(); clickDay(d); }}>
        <span className="dp-n">{d.getDate()}</span>
      </button>
    );
  }

  return (
    <div className="cdate" ref={wrapRef}>
      <button type="button" className="inp cdate-btn" onClick={toggle}>
        <span>{label}</span>
        <Icon name="calendar" size={16} />
      </button>
      {open && (
        <div className="datepop cdate-pop">
          <div className="dp-rail">
            {!isAppt && (
              <button type="button" className={`dp-preset ${mode === "week" ? "active" : ""}`} onClick={(e) => setModePreset("week", e)}>
                Week
              </button>
            )}
            <button type="button" className={`dp-preset ${mode === "custom" ? "active" : ""}`} onClick={(e) => setModePreset("custom", e)}>
              Custom
            </button>
          </div>
          <div className="dp-main">
            <div className="dp-title">{mode === "week" ? "Select Week" : "Select Dates"}</div>
            <div className="dp-head">
              <button type="button" className="dp-nav" onClick={(e) => { e.stopPropagation(); setViewMonth(new Date(base.getFullYear(), base.getMonth() - 1, 1)); }}>
                <Icon name="chevronLeft" size={16} />
              </button>
              <span className="dp-mon">{MONTHS[base.getMonth()]}</span>
              <span className="dp-yr">{base.getFullYear()}</span>
              <button type="button" className="dp-nav" onClick={(e) => { e.stopPropagation(); setViewMonth(new Date(base.getFullYear(), base.getMonth() + 1, 1)); }}>
                <Icon name="chevron" size={16} />
              </button>
            </div>
            <div className="dp-grid">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="dp-dow">{d}</div>
              ))}
              {cells}
            </div>
            <div className="dp-foot">
              <span className="dp-range">{sel.size} date{sel.size !== 1 ? "s" : ""} selected</span>
              <button type="button" className="dp-cancel" onClick={(e) => { e.stopPropagation(); setOpen(false); }}>Cancel</button>
              <button type="button" className="dp-apply" onClick={apply}>Add</button>
            </div>
          </div>
        </div>
      )}
      {showPills && (
        <div className="cdate-pills">
          {dObjs.map((d) => (
            <span key={keyOf(d)} className="cdate-pill">
              {mdy(d)}
              <button type="button" aria-label="Remove" onClick={(e) => removePill(e, keyOf(d))}>&times;</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- single-date picker (reuses cdate calendar styles) ---------------- */
export function SingleDatePicker({ value, min, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    const d = value ? parseISO(value) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && value) {
      const d = parseISO(value);
      setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    setOpen((o) => !o);
  };

  const minDate = min ? parseISO(min) : null;
  const selDate = value ? parseISO(value) : null;
  const base = new Date(viewMonth);
  const gridStart = (() => {
    const x = new Date(base.getFullYear(), base.getMonth(), 1);
    x.setDate(x.getDate() - x.getDay());
    x.setHours(0, 0, 0, 0);
    return x;
  })();
  const today = new Date();
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = addDays(gridStart, i);
    const out = d.getMonth() !== base.getMonth();
    const disabled = minDate && d < minDate;
    const cls = [
      "dp-cell",
      out ? "out" : "",
      sameDay(d, today) ? "today" : "",
      selDate && sameDay(d, selDate) ? "day-sel" : "",
      disabled ? "dp-off" : "",
    ]
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    cells.push(
      <button
        key={i}
        type="button"
        className={cls}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          onChange(keyOf(d));
          setOpen(false);
        }}
      >
        <span className="dp-n">{d.getDate()}</span>
      </button>
    );
  }

  const label = selDate
    ? `${DOW[selDate.getDay()]}, ${MON_SHORT[selDate.getMonth()]} ${selDate.getDate()}, ${selDate.getFullYear()}`
    : "Select a date";

  return (
    <div className="cdate" ref={wrapRef}>
      <button type="button" className="inp cdate-btn" onClick={toggle}>
        <span>{label}</span>
        <Icon name="calendar" size={16} />
      </button>
      {open && (
        <div className="datepop cdate-pop dp-single">
          <div className="dp-main">
            <div className="dp-title">Select date</div>
            <div className="dp-head">
              <button type="button" className="dp-nav" onClick={(e) => { e.stopPropagation(); setViewMonth(new Date(base.getFullYear(), base.getMonth() - 1, 1)); }}>
                <Icon name="chevronLeft" size={16} />
              </button>
              <span className="dp-mon">{MONTHS[base.getMonth()]}</span>
              <span className="dp-yr">{base.getFullYear()}</span>
              <button type="button" className="dp-nav" onClick={(e) => { e.stopPropagation(); setViewMonth(new Date(base.getFullYear(), base.getMonth() + 1, 1)); }}>
                <Icon name="chevron" size={16} />
              </button>
            </div>
            <div className="dp-grid">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="dp-dow">{d}</div>
              ))}
              {cells}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- generic styled dropdown (cdd) ---------------- */
function Cdd({ placeholder, value, options, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const sel = options.find((o) => o.key === value);
  return (
    <div className={`cdd ${open ? "open" : ""}`} ref={ref}>
      <button type="button" className="cdd-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {sel ? <span>{sel.name}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Icon name="chevronDown" size={16} />
      </button>
      <div className="cdd-menu">
        {options.map((o) => (
          <div key={o.key} className={`cdd-opt ${o.key === value ? "sel" : ""}`} onClick={() => { onSelect(o); setOpen(false); }}>
            <div className="co-name">{o.name}</div>
            {o.sub ? <div className="co-sub">{o.sub}</div> : null}
            <span className="co-chk"><Icon name="check" size={15} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- single-select dropdown (Client / Facility / Staff) ---------------- */
// Same look as the Shift (Cdd) dropdown, for plain {value,label} option lists.
export function SelectDD({ placeholder, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const sel = options.find((o) => o.value === value);
  return (
    <div className={`cdd ${open ? "open" : ""}`} ref={ref}>
      <button type="button" className="cdd-btn" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {sel ? <span>{sel.label}</span> : <span className="cdd-ph">{placeholder}</span>}
        <Icon name="chevronDown" size={16} />
      </button>
      <div className="cdd-menu">
        {options.map((o) => (
          <div key={o.value || "__empty"} className={`cdd-opt ${o.value === value ? "sel" : ""}`} onClick={() => { onChange(o.value); setOpen(false); }}>
            <div className="co-name">{o.label}</div>
            <span className="co-chk"><Icon name="check" size={15} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- service multiselect ---------------- */
function ServiceMultiSelect({ options, value, onChange, placeholder = "Select services..." }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const selected = options.filter((o) => value.includes(o.value));
  return (
    <div className="multiselect" ref={ref}>
      <div className={`ms-control ${open ? "open" : ""}`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
        {selected.length === 0 ? (
          <span className="ms-placeholder">{placeholder}</span>
        ) : (
          selected.map((o) => (
            <span className="pill" key={o.value}>
              <span>{o.label}</span>
              <button type="button" className="pill-x" onClick={(e) => { e.stopPropagation(); toggle(o.value); }}>&times;</button>
            </span>
          ))
        )}
        <span className="ms-arrow"><Icon name="chevronDown" size={16} /></span>
      </div>
      <div className={`ms-dropdown ${open ? "open" : ""}`}>
        <div className="ms-list">
          {options.map((o) => (
            <label
              key={o.value}
              className={`ms-option ${value.includes(o.value) ? "selected" : ""}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(o.value); }}
            >
              <span className="ms-checkbox"><Icon name="check" size={13} /></span>
              <span className="ms-name">{o.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CreateShiftDrawer({
  opened,
  onClose,
  onCreated,
  kind = "schedule",
  facilities = [],
  staff = [],
  clients = [],
  services = [],
  shifts = [],
  defaultDate,
  presetType,
  presetClient,
  presetFacility,
  editShift = null,
}) {
  const isEdit = !!editShift;
  const isAppt = isEdit ? editShift.kind === "appointment" : kind === "appointment";
  const defaultISO = isoDate(defaultDate || new Date());

  const [type, setType] = useState("client");
  const [facility, setFacility] = useState("");
  const [staffId, setStaffId] = useState("");
  const [clientIds, setClientIds] = useState([]);
  const [serviceVals, setServiceVals] = useState([]);
  const [apptTitle, setApptTitle] = useState("");
  const [dates, setDates] = useState([defaultISO]);
  const [template, setTemplate] = useState(null); // null until a shift template / Custom chosen
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [breakMin, setBreakMin] = useState("0");
  const [allDay, setAllDay] = useState(false);
  const [schedEntries, setSchedEntries] = useState([]);
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  // Weekly-repeat end date (YYYY-MM-DD). Editable; defaults to 8 weeks out.
  const [repeatUntil, setRepeatUntil] = useState("");
  const [templates, setTemplates] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  // Recurring-series edit scope: "single" (this occurrence) or "future".
  const [editScope, setEditScope] = useState("single");
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (!opened) return;
    if (isEdit) {
      // Prefill from the shift being edited (single occurrence).
      const svcLabels = (editShift.service || "").split(",").map((s) => s.trim()).filter(Boolean);
      const ids = editShift.clients?.length
        ? editShift.clients.map((c) => c._id || c.id || c)
        : editShift.client
        ? [idOf(editShift.client)]
        : [];
      const union = [];
      clients
        .filter((c) => ids.includes(c.value))
        .forEach((c) => (c.services || []).forEach((o) => {
          if (!union.some((x) => x.value === o.value)) union.push(o);
        }));
      const svcOpts = union.length ? union : services;
      setType(editShift.type || "client");
      setFacility(idOf(editShift.facility) || "");
      setStaffId(idOf(editShift.staff) || "");
      setClientIds(ids);
      setServiceVals(svcOpts.filter((o) => svcLabels.includes(o.label)).map((o) => o.value));
      setApptTitle(editShift.apptTitle || "");
      setDates([isoDate(new Date(editShift.date))]);
      setTemplate("custom"); // show time fields, seeded from the shift's own times
      setStart(minToHHMM(editShift.startMin));
      setEnd(minToHHMM(editShift.endMin));
      setBreakMin("0");
      setAllDay(!!editShift.allDay);
      setSchedEntries([]);
      setRepeatWeekly(false);
      setWarnings([]);
      setEditScope("single");
      setNoteText("");
      return;
    }
    setType(presetType || "client");
    setFacility(presetFacility || "");
    setStaffId("");
    setClientIds(presetClient ? [presetClient] : []);
    setServiceVals([]);
    setApptTitle("");
    setDates([defaultISO]);
    setTemplate(null);
    setStart("09:00");
    setEnd("17:00");
    setBreakMin("0");
    setAllDay(false);
    setSchedEntries([]);
    setRepeatWeekly(false);
    setWarnings([]);
    setEditScope("single");
    setNoteText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editShift]);

  // Show the full list of shift templates (not just the picked facility's), so
  // every saved shift is pickable regardless of the selected facility.
  useEffect(() => {
    let active = true;
    if (!opened) return undefined;
    schedulerService.getTemplates().then((t) => {
      if (active) setTemplates(Array.isArray(t) ? t : []);
    });
    return () => {
      active = false;
    };
  }, [opened]);

  const primary = dates[dates.length - 1];
  const showTime = isAppt || template != null;
  const startMin = allDay ? 8 * 60 : toMin(start);
  const endMin = allDay ? 18 * 60 : toMin(end);

  // live conflict / overtime check
  useEffect(() => {
    let active = true;
    // When editing, the shift being changed overlaps itself, so the conflict
    // check would always fire a false positive — skip it.
    if (isEdit) {
      setWarnings([]);
      return undefined;
    }
    if (staffId && !allDay && startMin != null && endMin != null && endMin > startMin) {
      schedulerService
        .checkConflicts({ staff: staffId, date: `${primary}T12:00:00`, startMin, endMin })
        .then((w) => active && setWarnings(w || []));
    } else {
      setWarnings([]);
    }
    return () => {
      active = false;
    };
  }, [staffId, primary, startMin, endMin, allDay, isEdit]);

  const duration = useMemo(() => {
    if (allDay) return "All day";
    if (startMin == null || endMin == null) return "-";
    const mins = endMin - startMin - Number(breakMin || 0);
    if (mins <= 0) return "-";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }, [allDay, startMin, endMin, breakMin]);

  const tmplOptions = useMemo(
    () => [
      ...templates
        .filter((t) => t.enabled !== false)
        .map((t) => ({ key: t._id || t.id, name: t.name, sub: fmtRange(t.startMin, t.endMin), startMin: t.startMin, endMin: t.endMin })),
      { key: "custom", name: "Custom", sub: "Enter times manually" },
    ],
    [templates]
  );
  const breakOptions = [
    { key: "0", name: "No break" },
    { key: "30", name: "30 min unpaid" },
    { key: "60", name: "60 min unpaid" },
  ];

  const pickTemplate = (o) => {
    setTemplate(o.key);
    if (o.key !== "custom" && o.startMin != null && o.endMin != null) {
      setStart(minToHHMM(o.startMin));
      setEnd(minToHHMM(o.endMin));
    }
  };

  // staff preview
  // Service options are the union of the selected clients' own billable
  // services (deduped); before any client is picked — or when none of them has
  // services — fall back to the provider-wide list.
  const serviceOptions = useMemo(() => {
    const union = [];
    clients
      .filter((c) => clientIds.includes(c.value))
      .forEach((c) => (c.services || []).forEach((o) => {
        if (!union.some((x) => x.value === o.value)) union.push(o);
      }));
    return union.length ? union : services;
  }, [clientIds, clients, services]);
  // drop any selected services that aren't valid for the chosen clients
  useEffect(() => {
    setServiceVals((prev) => prev.filter((v) => serviceOptions.some((o) => o.value === v)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds]);

  const selStaff = staff.find((s) => s.value === staffId);
  const staffWeekHrs = useMemo(() => {
    if (!staffId) return 0;
    const ws = startOfWeek(parseISO(primary));
    const mins = (shifts || [])
      .filter((s) => idOf(s.staff) === staffId && !s.allDay && sameDay(startOfWeek(new Date(s.date)), ws))
      .reduce((a, s) => a + Math.max(0, (s.endMin - s.startMin) / 60), 0);
    return mins;
  }, [staffId, primary, shifts]);

  const addToSchedule = () => {
    let s;
    let e;
    if (allDay) {
      s = 8 * 60;
      e = 18 * 60;
    } else {
      s = toMin(start);
      e = toMin(end);
      if (s == null || e == null || e <= s) return;
    }
    const next = [...schedEntries];
    dates.forEach((iso) => {
      const d = parseISO(iso);
      if (next.some((x) => sameDay(x.date, d) && x.startMin === s && x.endMin === e)) return;
      next.push({ date: d, startMin: s, endMin: e, allDay });
    });
    next.sort((a, b) => a.date - b.date || a.startMin - b.startMin);
    setSchedEntries(next);
    setDates([primary]);
  };

  const removeEntry = (i) => setSchedEntries((arr) => arr.filter((_, idx) => idx !== i));

  const repeatDaysLabel = useMemo(() => {
    const names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const uniq = [...new Set(schedEntries.map((e) => e.date.getDay()))].sort((a, b) => a - b).map((i) => names[i]);
    if (uniq.length <= 1) return uniq[0] || "";
    if (uniq.length === 2) return uniq.join(" and ");
    return `${uniq.slice(0, -1).join(", ")} and ${uniq[uniq.length - 1]}`;
  }, [schedEntries]);

  // The repeat can't end before the last scheduled day.
  const repeatMinDate = useMemo(() => {
    if (!schedEntries.length) return primary;
    return keyOf(new Date(Math.max(...schedEntries.map((e) => e.date.getTime()))));
  }, [schedEntries, primary]);

  const repeatUntilLabel = useMemo(() => {
    if (!repeatUntil) return "";
    const d = parseISO(repeatUntil);
    return `${MON_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }, [repeatUntil]);

  // Default the end date to 8 weeks past the last scheduled day when repeat is
  // switched on; clear it when switched off. Stays editable in between.
  useEffect(() => {
    if (!repeatWeekly) {
      if (repeatUntil) setRepeatUntil("");
      return;
    }
    if (repeatUntil) return;
    setRepeatUntil(keyOf(addDays(parseISO(repeatMinDate), 56)));
  }, [repeatWeekly, repeatMinDate, repeatUntil]);

  const canSave = useMemo(() => {
    if (!facility) return false;
    if (isAppt && !apptTitle.trim()) return false;
    if (type === "client" && clientIds.length < 1) return false;
    if (schedEntries.length) return true;
    if (!showTime) return false;
    if (allDay) return true;
    return startMin != null && endMin != null && endMin > startMin;
  }, [facility, isAppt, apptTitle, type, clientIds, schedEntries.length, showTime, allDay, startMin, endMin]);

  const save = async () => {
    setBusy(true);
    const serviceStr =
      type === "client" ? serviceOptions.filter((o) => serviceVals.includes(o.value)).map((o) => o.label).join(", ") : "";
    const mk = (d, sMin, eMin, ad) => ({
      type,
      kind: isAppt ? "appointment" : "shift",
      apptTitle: isAppt ? apptTitle.trim() : "",
      facility,
      staff: staffId || null,
      // `client` stays = clients[0] for backwards compatibility.
      client: type === "client" ? clientIds[0] || null : null,
      clients: type === "client" ? clientIds : [],
      service: serviceStr,
      date: `${keyOf(d)}T12:00:00`,
      startMin: sMin,
      endMin: eMin,
      allDay: !!ad,
      recurrence:
        repeatWeekly
          ? { freq: "weekly", daysOfWeek: [d.getDay()], until: `${repeatUntil || keyOf(addDays(d, 56))}T12:00:00` }
          : { freq: "none" },
      note: noteText.trim() || undefined,
    });

    // Editing updates the single existing shift in place (no multi-date / repeat).
    if (isEdit) {
      const payload = mk(parseISO(primary), startMin, endMin, allDay);
      delete payload.recurrence; // don't rewrite recurrence when editing one occurrence
      if (editShift.recurrenceGroupId) {
        // "future" applies the same non-date changes to later occurrences too.
        payload.scope = editScope === "future" ? "future" : "single";
      }
      const ok = await schedulerService.updateShift(editShift._id || editShift.id, payload);
      if (!ok) {
        setBusy(false);
        return;
      }
      setNoteText("");
      setBusy(false);
      onCreated?.();
      onClose?.();
      return;
    }

    let payloads;
    if (schedEntries.length) {
      payloads = schedEntries.map((e) => mk(e.date, e.startMin, e.endMin, e.allDay));
    } else {
      payloads = [mk(parseISO(primary), startMin, endMin, allDay)];
    }
    // create sequentially; refresh + close once
    for (let i = 0; i < payloads.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await schedulerService.createShift(payloads[i]);
    }
    setBusy(false);
    onCreated?.();
    onClose?.();
  };

  // Cancel the whole weekly series from this occurrence onward: removes this
  // shift and all future ones, keeping past shifts intact.
  const stopRecurrence = async () => {
    const ok = await swal({
      title: "Cancel this recurring shift?",
      text: "This shift and all future occurrences will be removed. Past shifts are kept.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Keep", "Cancel recurring shift"],
    });
    if (!ok) return;
    setBusy(true);
    await schedulerService.deleteShift(editShift._id || editShift.id, "future", () => {
      setBusy(false);
      onCreated?.();
      onClose?.();
    });
    setBusy(false);
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={680}
      withCloseButton={false}
      padding={0}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      overlayProps={{ opacity: 0.55, blur: 3 }}
      zIndex={1000}
    >
      <div className="sd-root">
        <div className="sd-head">
          <div>
            <h3>{isEdit ? (isAppt ? "Edit Appointment" : "Edit Schedule") : isAppt ? "Add Appointment" : "Add Schedule"}</h3>
          </div>
          <button type="button" className="sd-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="sd-body sd-create">
          {/* ---------- Assignment ---------- */}
          <div className="cs-section">
            {selStaff && (
              <div className="staff-preview">
                <div className="sp-av">
                  {selStaff.photo ? <img src={selStaff.photo} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initials(selStaff.label)}
                </div>
                <div className="sp-info">
                  <div className="sp-name">{selStaff.label}</div>
                  <div className="sp-meta">Assigned staff member</div>
                </div>
                <div className={`sp-stat ${staffWeekHrs > 40 ? "ot" : ""}`}>
                  {staffWeekHrs % 1 ? staffWeekHrs.toFixed(1) : staffWeekHrs}h this week{staffWeekHrs > 40 ? " · OT" : ""}
                </div>
              </div>
            )}

            {isAppt && (
              <div className="field">
                <label>Appointment title <span className="req">*</span></label>
                <input className="inp" placeholder="e.g. Care plan review, Family meeting" value={apptTitle} onChange={(e) => setApptTitle(e.target.value)} />
              </div>
            )}

            <div className="frow">
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Facility <span className="req">*</span></label>
                <SelectDD placeholder="Select a facility" value={facility} options={facilities} onChange={setFacility} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>
                  Staff member {isAppt ? <span style={{ fontSize: 11, fontWeight: 500, color: "var(--app-text-muted)" }}>(optional)</span> : <span className="req">*</span>}
                </label>
                <SelectDD placeholder="Unassigned" value={staffId} options={[{ value: "", label: "Unassigned" }, ...staff]} onChange={setStaffId} />
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div className="type-label">{isAppt ? "Appointment type" : "Shift Type"}</div>
              <div className="type-cards">
                <label>
                  <input type="radio" name="ftype" value="client" checked={type === "client"} onChange={() => setType("client")} />
                  <div className="type-card">
                    <div className="tc-ic"><Icon name="person" size={18} /></div>
                    <div className="tc-t">{isAppt ? "Client Appointment" : "Visit"}</div>
                  </div>
                </label>
                <label>
                  <input type="radio" name="ftype" value="facility" checked={type === "facility"} onChange={() => setType("facility")} />
                  <div className="type-card">
                    <div className="tc-ic"><Icon name="hospital" size={18} /></div>
                    <div className="tc-t">{isAppt ? "Facility Appointment" : "Facility"}</div>
                  </div>
                </label>
              </div>
            </div>

            {type === "client" && (
              <div style={{ marginTop: 16 }}>
                <div className="frow">
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Client <span className="req">*</span></label>
                    <ServiceMultiSelect placeholder="Select clients..." options={clients} value={clientIds} onChange={setClientIds} />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Service <span style={{ fontSize: 11, fontWeight: 500, color: "var(--app-text-muted)" }}>(optional)</span></label>
                    <ServiceMultiSelect options={serviceOptions} value={serviceVals} onChange={setServiceVals} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ---------- When ---------- */}
          <div className="cs-section">
            <div className="frow">
              <div className="field" style={{ marginBottom: 0 }}>
                <label>Date <span className="req">*</span></label>
                <CDatePicker dates={dates} onChange={setDates} isAppt={isAppt} />
              </div>
              {!isAppt && (
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Shift <span className="req">*</span></label>
                  <Cdd placeholder="Select a shift template" value={template} options={tmplOptions} onSelect={pickTemplate} />
                </div>
              )}
            </div>

            {showTime && (
              <div style={{ marginTop: 18 }}>
                {isAppt && (
                  <label className="allday-toggle">
                    <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
                    <span className="ad-box"><Icon name="check" size={13} /></span>
                    <span className="ad-txt">All day</span>
                  </label>
                )}
                {!allDay && (
                  <div className="time-row">
                    <div className="field" style={{ margin: 0 }}>
                      <label>Start <span className="req">*</span></label>
                      <input className="inp" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
                    </div>
                    <div className="field" style={{ margin: 0 }}>
                      <label>End <span className="req">*</span></label>
                      <input className="inp" type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
                    </div>
                    <div>
                      <div className="dur-lbl">Duration</div>
                      <div className="dur-chip"><Icon name="clock" size={14} /><span>{duration}</span></div>
                    </div>
                  </div>
                )}
                {!isAppt && (
                  <div className="field" style={{ marginTop: 12, marginBottom: 0 }}>
                    <label>Break</label>
                    <Cdd placeholder="No break" value={breakMin} options={breakOptions} onSelect={(o) => setBreakMin(o.key)} />
                  </div>
                )}
                {!isEdit && (
                  <>
                    <button type="button" className="btn btn-soft btn-sm add-sched-btn" onClick={addToSchedule}>
                      <Icon name="plus" size={15} /> Add another date
                    </button>
                    <div style={{ fontSize: 11.5, color: "var(--app-text-muted)", textAlign: "center", marginTop: 6 }}>
                      Scheduling a single day? Just press Create below. Use this to add more dates at once.
                    </div>
                  </>
                )}
              </div>
            )}

            {schedEntries.length > 0 && (
              <div className="sched-entries">
                <div className="se-head">
                  <span className="se-head-t">
                    <Icon name="calendar" size={14} /> Schedule being created
                    <span className="se-head-n">{schedEntries.length} shift{schedEntries.length > 1 ? "s" : ""}</span>
                  </span>
                  <label className="se-repeat">
                    <input type="checkbox" checked={repeatWeekly} onChange={(e) => setRepeatWeekly(e.target.checked)} />
                    Repeat weekly
                  </label>
                </div>
                {schedEntries.map((e, i) => (
                  <div key={`${keyOf(e.date)}-${e.startMin}`} className="sched-entry">
                    <div className="se-day">
                      <span className="se-dow">{DOW[e.date.getDay()]}</span>
                      <span className="se-date">{MON_SHORT[e.date.getMonth()]} {e.date.getDate()}</span>
                    </div>
                    <div className="se-body">
                      <div className="se-time">{e.allDay ? "All day" : fmtRange(e.startMin, e.endMin)}</div>
                      <div className="se-meta">
                        {e.allDay
                          ? isAppt ? "appointment" : "shift"
                          : `${(() => { const h = (e.endMin - e.startMin) / 60; return h % 1 === 0 ? h : h.toFixed(1); })()}h ${isAppt ? "appointment" : "shift"}`}
                      </div>
                    </div>
                    <button type="button" className="se-x" aria-label="Remove" onClick={() => removeEntry(i)}>
                      <Icon name="x" size={14} />
                    </button>
                  </div>
                ))}
                {repeatWeekly && (
                  <div className="cs-repeat-end">
                    <label>Repeat until</label>
                    <SingleDatePicker value={repeatUntil} min={repeatMinDate} onChange={setRepeatUntil} />
                  </div>
                )}
                {repeatWeekly && (
                  <div className="cs-repeat-msg">
                    <Icon name="check" size={15} /> This schedule will repeat every {repeatDaysLabel}
                    {repeatUntilLabel ? ` until ${repeatUntilLabel}` : ""}.
                  </div>
                )}
              </div>
            )}

            {warnings.map((w, i) => (
              <div key={i} className={`cs-warn ${i === 0 ? "amber" : "red"}`}>
                <Icon name="alert" size={14} /> {w.message}
              </div>
            ))}
          </div>

          {/* ---------- Notes ---------- */}
          <div className="cs-section">
            <div className="field" style={{ marginBottom: 10 }}>
              <label>Notes</label>
              <textarea
                className="inp"
                placeholder="Add care instructions or context..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </div>
            <div className="cs-info">
              {isAppt ? <Icon name="user" size={15} /> : <Icon name="pin" size={15} />}
              {isAppt
                ? "All appointments are shown in calendars based on access permissions to the Facility or Client."
                : "EVV and clock-in settings from the facility apply to this shift. They cannot be overwritten here."}
            </div>
          </div>

          {/* ---------- Recurring-series edit scope ---------- */}
          {isEdit && editShift?.recurrenceGroupId ? (
            <div className="cs-section">
              <div className="type-label">Apply to</div>
              <div className="scope-radios">
                <label className="scope-radio">
                  <input type="radio" name="editScope" value="single" checked={editScope === "single"} onChange={() => setEditScope("single")} />
                  This shift only
                </label>
                <label className="scope-radio">
                  <input type="radio" name="editScope" value="future" checked={editScope === "future"} onChange={() => setEditScope("future")} />
                  This and future shifts
                </label>
              </div>
              <div className="scope-hint">Date changes apply only to this shift.</div>
              <button type="button" className="btn btn-danger-ghost btn-sm cs-stop-recur" onClick={stopRecurrence} disabled={busy}>
                <Icon name="x" size={15} /> Cancel recurring shift
              </button>
            </div>
          ) : null}
        </div>

        <div className="sd-foot">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <span className="spacer" />
          <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!canSave || busy}>
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}
